import Vue from 'vue';
import {
    keyBy as _keyBy
    } from '@lodash';
import SpecialEvent from '@core/models/specials/SpecialEvent';
import StoreUtil from '@store/StoreUtil';
import { Commit, Dispatch, ActionContext } from 'vuex';
import SpecialsService from '@core/services/specials/SpecialsService';
import config from '@config';
import { getTimeFilterFromSlug, getTimeSpanForTimeFilter } from '@shared/timeFilter/helpers';
import { TimeFilter } from '@shared/timeFilter/enums';
import { IState } from '@app/store';

type SpecialsChunk = { name: string, id: number };

const SET_SPECIAL_EVENTS = 'SET_SPECIAL_EVENTS';
const SET_SPECIAL_EVENT = 'SET_SPECIAL_EVENT';
const SET_IS_LOADING = 'SET_IS_LOADING';
const SET_IS_INITIAL_FETCH_FINISHED = 'SET_IS_INITIAL_FETCH_FINISHED';
const SET_LAST_FETCH_TIMESTAMP = 'SET_LAST_FETCH_TIMESTAMP';

export interface ISpecialsState {
    events: Record<number, SpecialsChunk | SpecialEvent>;
    isLoading: boolean;
    isInitialFetchFinished: boolean;
    lastFetchTimestamp: number | null;
}

function defaultState(): ISpecialsState {
    return {
        events: {},
        isLoading: false,
        isInitialFetchFinished: false,
        lastFetchTimestamp: null,
    };
}

export default {
    namespaced: true,
    state: defaultState(),
    mutations: {
        [SET_SPECIAL_EVENTS]: (state: ISpecialsState, events: Record<number, SpecialEvent>) => {
            state.events = {
                ...state.events,
                ...events,
            };
        },
        [SET_SPECIAL_EVENT]: (state: ISpecialsState, event: SpecialEvent) => {
            Vue.set(<any>state.events, event.id, event);
        },
        [SET_IS_LOADING]: StoreUtil.createSimpleMutator('isLoading'),
        [SET_IS_INITIAL_FETCH_FINISHED]: StoreUtil.createSimpleMutator('isInitialFetchFinished'),
        [SET_LAST_FETCH_TIMESTAMP]: StoreUtil.createSimpleMutator('lastFetchTimestamp'),
    },
    getters: {
        specialEventsMap: StoreUtil.createSimpleGetter('events'),
        specialEvents: (state: ISpecialsState) => Object.values(state.events),
        specialEventById: (state: ISpecialsState) => (id: number) => state.events[id],
        isLoading: StoreUtil.createSimpleGetter('isLoading'),
        isInitialFetchFinished: StoreUtil.createSimpleGetter('isInitialFetchFinished'),
        timeFilter(state: any, getters: any, rootState: IState) {
            const timeFilterSlug = rootState.route.params.timeFilter;
            return timeFilterSlug ? getTimeFilterFromSlug(timeFilterSlug) : TimeFilter.all;
        }
    },
    actions: {
        fetchAllSpecialEventsIfIntervalPassed: (
            { commit, state, dispatch }: { commit: Commit, state: ISpecialsState, dispatch: Dispatch }) => {
            if (shouldInvokeAction(state.lastFetchTimestamp, config.app.specialOffer.fetchInterval)) {
                dispatch('fetchSpecialEvents', getTimeSpanForTimeFilter(TimeFilter.all));
                commit(SET_LAST_FETCH_TIMESTAMP, Date.now());
            }
        },
        loadSpecialOffer(context: ActionContext<any, IState>) {
            const timeFilter = context.getters.timeFilter;
            const timeSpan = getTimeSpanForTimeFilter(timeFilter);
            return context.dispatch('fetchSpecialEvents', timeSpan);
        },
        fetchSpecialEvents: StoreUtil.asyncErrorGuard(async(
            { commit }: { commit: Commit },
            timeSpan: { startDate: Date, endDate: Date }
        ) => {
            commit(SET_IS_LOADING, true);
            try {
                commit(
                    SET_SPECIAL_EVENTS,
                    _keyBy(
                        await SpecialsService.getInstance().getSpecialOffer(timeSpan.startDate, timeSpan.endDate),
                        'id'
                    )
                );
                commit(SET_IS_LOADING, false);
                commit(SET_IS_INITIAL_FETCH_FINISHED, true);
            } catch (e) {
                commit(SET_IS_LOADING, false);
                throw e;
            }
        }),
        fetchSpecialDetail: StoreUtil.asyncErrorGuard(async({ commit }: { commit: Commit }, matchId: number) => {
            try {
                const specialEvent = await SpecialsService.getInstance().getEvent(matchId as number);
                commit(SET_SPECIAL_EVENT, specialEvent);
            } catch (e) {
                throw e;
            }
        }),
    }
};

function shouldInvokeAction(timestamp: number | null, interval: number) {
    if (!timestamp) {
        return true;
    }
    return Date.now() > timestamp + interval;
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--12!./src/app/store/modules/data/specialOffer/specialsIndex.ts